import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, Loader2, ShieldCheck, BarChart3, CalendarDays } from "lucide-react";
import api, { fmtApiError } from "./api";

export default function ConnectLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("signin");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotDone, setForgotDone] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotDone(true);
    } catch (err) {
      setError(fmtApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const [reqForm, setReqForm] = useState({ name: "", email: "", phone: "", clinic: "", partnership: "Dyntra Clinic Partner", role: "Clinic Manager" });
  const [reqDone, setReqDone] = useState(false);
  const [clinicOptions, setClinicOptions] = useState([]);

  useEffect(() => {
    if (mode === "request" && clinicOptions.length === 0) {
      api.get("/clinics").then((r) => setClinicOptions(r.data)).catch(() => {});
    }
  }, [mode]);

  useEffect(() => {
    if (localStorage.getItem("dyntra_connect_token")) {
      nav("/connect/app", { replace: true });
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("dyntra_connect_token", data.token);
      localStorage.setItem("dyntra_connect_user", JSON.stringify(data.user));
      nav("/connect/app", { replace: true });
    } catch (err) {
      setError(fmtApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...reqForm };
      if (payload.clinic === "Other / Not Listed") payload.clinic = payload.customClinic || "Other";
      delete payload.customClinic;
      await api.post("/access-requests", payload);
      setReqDone(true);
    } catch (err) {
      setError(fmtApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="connect-login" className="min-h-screen grid lg:grid-cols-2 bg-ivory">
      <div className="hidden lg:flex flex-col justify-between bg-pine-deep text-ivory p-12 relative overflow-hidden grain">
        <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-teal-brand/15 blur-3xl" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-3">
            <img src="/dyntra-logo.jpg" alt="Dyntra" className="h-12 w-12 rounded-full object-cover ring-2 ring-gold/60" />
            <div>
              <p className="font-display text-2xl font-semibold">Dyntra <span className="text-teal-brand italic">Connect</span></p>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold">Dental Clinic Management · Made Smarter</p>
            </div>
          </Link>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-medium leading-tight">
            Your clinic,<br /><span className="italic text-gold">beautifully organised.</span>
          </h1>
          <ul className="mt-8 space-y-4 text-sm text-ivory/75">
            <li className="flex items-center gap-3"><CalendarDays className="h-5 w-5 text-gold" /> Website bookings land here in real time</li>
            <li className="flex items-center gap-3"><BarChart3 className="h-5 w-5 text-gold" /> Revenue, inventory &amp; staff at a glance</li>
            <li className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-gold" /> Secure access for your clinic team</li>
          </ul>
        </div>
        <p className="relative text-xs text-ivory/40">A unit of DyntraStore Pvt. Ltd. · Powered by the Dyntra Store ecosystem</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/dyntra-logo.jpg" alt="Dyntra" className="h-11 w-11 rounded-full object-cover ring-2 ring-gold/60" />
            <p className="font-display text-xl font-semibold text-pine-deep">Dyntra <span className="text-teal-brand italic">Connect</span></p>
          </div>
          <div className="rounded-3xl bg-white border border-pine/10 shadow-lift p-8 md:p-10">
            <div className="flex rounded-full border border-pine/15 overflow-hidden mb-6">
              <button data-testid="connect-mode-signin" onClick={() => { setMode("signin"); setError(""); }}
                className={`flex-1 py-2.5 text-xs font-bold transition-colors ${mode === "signin" ? "bg-pine text-ivory" : "bg-white text-pine"}`}>
                Sign In
              </button>
              <button data-testid="connect-mode-request" onClick={() => { setMode("request"); setError(""); }}
                className={`flex-1 py-2.5 text-xs font-bold transition-colors ${mode === "request" ? "bg-pine text-ivory" : "bg-white text-pine"}`}>
                Request Access
              </button>
            </div>

            {mode === "signin" ? (
              <>
                <h2 className="font-display text-2xl font-semibold text-pine-deep">Sign in to Dyntra Connect</h2>
                <p className="mt-1.5 text-sm text-slate-500">Clinic management for Dyntra Clinics partners.</p>
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Email</label>
                <div className="mt-1.5 relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-pine/40" />
                  <input
                    data-testid="connect-email"
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@dyntrastore.in"
                    className="h-12 w-full rounded-xl border border-pine/15 bg-white pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Password</label>
                <div className="mt-1.5 relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-pine/40" />
                  <input
                    data-testid="connect-password"
                    type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className="h-12 w-full rounded-xl border border-pine/15 bg-white pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine"
                  />
                </div>
              </div>
              {error && <p data-testid="connect-login-error" className="text-sm font-semibold text-red-600">{error}</p>}
              <button
                type="submit"
                data-testid="connect-login-submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-bold text-ivory shadow-lift transition-all duration-300 hover:bg-pine-deep disabled:opacity-60"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                {busy ? "Signing in..." : "Sign In"}
              </button>
              <button
                type="button"
                data-testid="connect-forgot-link"
                onClick={() => { setMode("forgot"); setError(""); }}
                className="w-full text-center text-xs font-bold text-teal-brand hover:text-pine transition-colors"
              >
                Forgot password?
              </button>
            </form>
            <Link to="/" data-testid="connect-back-home" className="mt-6 block text-center text-xs font-bold text-teal-brand hover:text-pine transition-colors">
              ← Back to Dyntra Clinics website
            </Link>
            <p className="mt-3 text-center text-xs text-slate-400">
              New clinic partner? Switch to <button type="button" onClick={() => setMode("request")} className="font-bold text-gold hover:text-pine">Request Access</button> — accounts are approved by the Dyntra Super Admin.
            </p>
              </>
            ) : mode === "forgot" ? (
              <>
                <h2 className="font-display text-2xl font-semibold text-pine-deep">Forgot Password</h2>
                <p className="mt-1.5 text-sm text-slate-500">Enter your account email — we'll send a reset link valid for 1 hour.</p>
                {forgotDone ? (
                  <div data-testid="forgot-success" className="mt-7 rounded-2xl bg-teal-soft p-6 text-center">
                    <p className="font-display text-lg font-semibold text-pine-deep">Check your inbox</p>
                    <p className="mt-1 text-sm text-slate-600">If this email has an account, a reset link has been sent.</p>
                    <button onClick={() => { setMode("signin"); setForgotDone(false); }} className="mt-4 text-xs font-bold text-teal-brand hover:text-pine">← Back to Sign In</button>
                  </div>
                ) : (
                  <form onSubmit={submitForgot} className="mt-6 space-y-4">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Account Email</label>
                      <div className="mt-1.5 relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-pine/40" />
                        <input data-testid="forgot-email" type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" className="h-12 w-full rounded-xl border border-pine/15 bg-white pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine" />
                      </div>
                    </div>
                    {error && <p data-testid="forgot-error" className="text-sm font-semibold text-red-600">{error}</p>}
                    <button type="submit" data-testid="forgot-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-bold text-ivory shadow-lift transition-all hover:bg-pine-deep disabled:opacity-60">
                      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                      {busy ? "Sending..." : "Email Reset Link"}
                    </button>
                    <button type="button" onClick={() => setMode("signin")} className="w-full text-center text-xs font-bold text-teal-brand hover:text-pine transition-colors">← Back to Sign In</button>
                  </form>
                )}
              </>
            ) : (
              <>
                <h2 className="font-display text-2xl font-semibold text-pine-deep">Request Access</h2>
                <p className="mt-1.5 text-sm text-slate-500">Dyntra Connect is for clinic partners — Dyntra or independent clinics. Franchise applicants do not get dashboard access. Your request goes to the Dyntra Super Admin for approval.</p>
                {reqDone ? (
                  <div data-testid="request-success" className="mt-7 rounded-2xl bg-teal-soft p-6 text-center">
                    <p className="font-display text-lg font-semibold text-pine-deep">Request received!</p>
                    <p className="mt-1 text-sm text-slate-600">The Dyntra Super Admin will review and approve your access. This has been sent to info@dyntra.store.</p>
                  </div>
                ) : (
                  <form onSubmit={submitRequest} className="mt-6 space-y-4">
                    {[
                      { k: "name", label: "Full Name", type: "text", ph: "Your full name", tid: "request-name" },
                      { k: "email", label: "Email", type: "email", ph: "you@example.com", tid: "request-email" },
                      { k: "phone", label: "Phone", type: "text", ph: "+91 ...", tid: "request-phone" },
                    ].map((f) => (
                      <div key={f.k}>
                        <label className="text-xs font-bold uppercase tracking-wider text-pine/60">{f.label}</label>
                        <input
                          data-testid={f.tid}
                          type={f.type} required value={reqForm[f.k]}
                          onChange={(e) => setReqForm({ ...reqForm, [f.k]: e.target.value })}
                          placeholder={f.ph}
                          className="mt-1.5 h-12 w-full rounded-xl border border-pine/15 bg-white px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Partnership Type</label>
                      <select data-testid="request-partnership" value={reqForm.partnership} onChange={(e) => setReqForm({ ...reqForm, partnership: e.target.value })}
                        className="mt-1.5 h-12 w-full rounded-xl border border-pine/15 bg-white px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine">
                        <option>Dyntra Clinic Partner</option>
                        <option>Independent / Non-Dyntra Clinic</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Clinic</label>
                      <select data-testid="request-clinic" required value={reqForm.clinic} onChange={(e) => setReqForm({ ...reqForm, clinic: e.target.value })}
                        className="mt-1.5 h-12 w-full rounded-xl border border-pine/15 bg-white px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine">
                        <option value="" disabled>Select your clinic</option>
                        {clinicOptions.map((c) => <option key={c.id} value={c.name}>{c.name} — {c.city}</option>)}
                        <option value="Other / Not Listed">Other / Not Listed</option>
                      </select>
                    </div>
                    {reqForm.clinic === "Other / Not Listed" && (
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Your Clinic Name</label>
                        <input data-testid="request-clinic-custom" required value={reqForm.customClinic || ""} onChange={(e) => setReqForm({ ...reqForm, customClinic: e.target.value })} placeholder="Clinic name & city" className="mt-1.5 h-12 w-full rounded-xl border border-pine/15 bg-white px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine" />
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-pine/60">Requested Role</label>
                      <select data-testid="request-role" value={reqForm.role} onChange={(e) => setReqForm({ ...reqForm, role: e.target.value })}
                        className="mt-1.5 h-12 w-full rounded-xl border border-pine/15 bg-white px-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pine">
                        <option>Clinic Admin</option>
                        <option>Clinic Manager</option>
                      </select>
                    </div>
                    {error && <p data-testid="request-error" className="text-sm font-semibold text-red-600">{error}</p>}
                    <button type="submit" data-testid="request-submit" disabled={busy}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold text-pine-deep shadow-lift transition-all duration-300 hover:bg-gold-soft disabled:opacity-60">
                      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                      {busy ? "Sending..." : "Submit Access Request"}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
